import { getAPI, postAPI, postMultipartAPI } from "../../../api/Networking";
import {
  InventoryAdjustmentApiError,
  InventoryAdjustmentRequest,
  InventoryAdjustmentResult,
  InventoryAdjustmentRowError,
  InventoryAuditLogFilters,
  InventoryAuditLogResponse,
  ProductTransactionFilters,
  ProductTransactionResponse,
  SkuTransferRequest,
} from "../../../models/SwageazyInventory/SwageazyInventoryAdjustment";

const BASE = "/v1/swageazy-inventory";

/**
 * Turns an axios / API failure into `{ message, code, rowErrors }` so the
 * popups can render row-level messages inline.
 */
export const parseInventoryAdjustmentError = (
  error: unknown,
  fallback = "Something went wrong! Please try again later.",
): InventoryAdjustmentApiError => {
  const err = error as {
    message?: string;
    response?: {
      data?: {
        message?: string;
        result?: {
          code?: string;
          details?: {
            row_errors?: InventoryAdjustmentRowError[];
            on_hand?: number;
            blocked?: number;
            warehouse_id?: string;
          };
        };
      };
    };
  };
  const data = err?.response?.data;
  const code = data?.result?.code;
  const details = data?.result?.details;
  const rowErrors = Array.isArray(details?.row_errors)
    ? details?.row_errors ?? []
    : [];

  let message = data?.message || err?.message || fallback;
  if (
    code === "BLOCKED_STOCK" &&
    details?.on_hand !== undefined &&
    details?.blocked !== undefined
  ) {
    message = `${message} (Warehouse ${details.warehouse_id ?? "—"}: on hand ${details.on_hand}, blocked ${details.blocked}. Release the block first.)`;
  }

  return { message, code, rowErrors };
};

export default class SwageazyInventoryAdjustmentAPI {
  createAdjustment = async (
    body: InventoryAdjustmentRequest,
  ): Promise<InventoryAdjustmentResult> => {
    try {
      const response = await postAPI(`${BASE}/adjustments`, body);
      return response.result as InventoryAdjustmentResult;
    } catch (error) {
      throw error;
    }
  };

  importAdjustmentCsv = async (
    file: File,
    reason: string,
  ): Promise<InventoryAdjustmentResult> => {
    try {
      const formData = new FormData();
      formData.append("csv_file", file);
      formData.append("reason", reason);
      const response = await postMultipartAPI(
        `${BASE}/adjustments/import-csv`,
        formData,
      );
      return response.result as InventoryAdjustmentResult;
    } catch (error) {
      throw error;
    }
  };

  createSkuTransfer = async (
    body: SkuTransferRequest,
  ): Promise<InventoryAdjustmentResult> => {
    try {
      const response = await postAPI(`${BASE}/sku-transfers`, body);
      return response.result as InventoryAdjustmentResult;
    } catch (error) {
      throw error;
    }
  };

  getAuditLogs = async (
    filters: InventoryAuditLogFilters,
  ): Promise<InventoryAuditLogResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page_no", String(filters.page_no));
      queryParams.append("limit", String(filters.limit));
      if (filters.product_id)
        queryParams.append("product_id", String(filters.product_id));
      if (filters.admin_id)
        queryParams.append("admin_id", String(filters.admin_id));
      if (filters.sku?.trim()) queryParams.append("sku", filters.sku.trim());
      if (filters.type) queryParams.append("type", filters.type);

      const response = await getAPI(
        `${BASE}/audit-logs?${queryParams.toString()}`,
      );
      return response.result as InventoryAuditLogResponse;
    } catch (error) {
      throw error;
    }
  };

  getProductTransactions = async (
    filters: ProductTransactionFilters,
  ): Promise<ProductTransactionResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page_no", String(filters.page_no));
      if (filters.sku?.trim()) queryParams.append("sku", filters.sku.trim());

      const response = await getAPI(
        `${BASE}/products/${filters.product_id}/transactions?${queryParams.toString()}`,
      );
      return response.result as ProductTransactionResponse;
    } catch (error) {
      throw error;
    }
  };
}
